// AccountStorage — the secure-ls old-selection cache (contract 07 §3/§7 "Kept"). Retyped from
// v3's `unknown` to a string-keyed persist-storage backend so it drops straight into
// pinia-plugin-persistedstate (module 01). A decrypt/tamper failure fails safe to `null`
// (treated as no cached selection, no crash) — the same pattern as auth's secure storage.
//
// FROZEN WIRE CONTRACT (ORP-18641). v3 and v4 serve different routes on the SAME origin during the
// proxy split, so they share one localStorage. `accountStore`/`workspaceStore` already resolve to
// v3's keys, so these ctor options are the remaining half of the invariant: same key + a different
// codec means each app's write destroys the other's blob.
//
// The two adapters below differ because **v3's two constructions differ**, not by preference:
//   • account   → v3 src/features/account/storage/AccountStorage.ts:3 `new SecureLS({ encodingType: 'aes' })`
//                 — no `isCompression` ⇒ the library default, TRUE.
//   • workspace → v3 src/stores/workspaceStore.ts:17 `new SecureLS({ isCompression: false })`
//                 — no `encodingType` ⇒ the library default, Base64.
// (Defaults: secure-ls 2.0.0 dist:216-217; both repos pin the same version.) Collapsing these back
// into one shared adapter re-breaks the boundary. See stores/__tests__/accountCutover.spec.ts.
import SecureLS from 'secure-ls'

// secure-ls ships a webpack-UMD CJS bundle whose module.exports is
// { __esModule, default: class }. Vite's production (rolldown) CJS interop is
// node-mode — this default import receives that whole exports object — while
// dev/vitest (esbuild prebundle) receive the class itself. Resolve both to the
// constructor; without this, `new SecureLS(...)` throws "not a constructor" in
// production builds only and these adapters silently stop persisting (same
// defect as auth's secureStorage — staging reload-logout, 2026-07-17).
const SecureLSCtor: typeof SecureLS =
  (SecureLS as unknown as { default?: typeof SecureLS }).default ?? SecureLS

/** Lazily construct one secure-ls per codec; `options` is a v3 wire contract, not a preference. */
function lazySecure(options: ConstructorParameters<typeof SecureLS>[0]): () => SecureLS {
  let ls: SecureLS | null = null
  return () => (ls ??= new SecureLSCtor(options))
}

const secure = lazySecure({ encodingType: 'aes' })
const secureWorkspace = lazySecure({ isCompression: false })

/** pinia-plugin-persistedstate storage backend (sync, string-valued). */
export interface PersistStorageLike {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
  removeItem: (key: string) => void
}

/** Wrap a secure-ls instance as a persist backend. A decrypt/tamper failure fails safe to `null`. */
function persistStorage(ls: () => SecureLS): PersistStorageLike {
  return {
    getItem: (key: string): string | null => {
      try {
        const value = ls().get(key)
        return value === '' || value == null ? null : (value as string)
      } catch {
        return null // tampered / decrypt failure — fail safe
      }
    },
    setItem: (key: string, value: string): void => {
      try {
        ls().set(key, value)
      } catch (cause) {
        // A swallowed write is a silently lost selection on the next reload —
        // keep the failure observable while staying fail-safe.
        console.warn('[account] secure-ls write failed; the selection will not persist.', cause)
      }
    },
    removeItem: (key: string): void => ls().remove(key),
  }
}

/** Backs `accountStore` — aes + compression (v3 AccountStorage.ts:3). */
export const accountPersistStorage: PersistStorageLike = persistStorage(secure)

/** Backs `workspaceStore` — Base64, no compression (v3 workspaceStore.ts:17). NOT interchangeable
 *  with `accountPersistStorage`: v3 writes these two keys with different codecs. */
export const workspacePersistStorage: PersistStorageLike = persistStorage(secureWorkspace)

export class AccountStorage {
  public static getItem(key: string): string | null {
    return accountPersistStorage.getItem(key)
  }

  public static setItem(key: string, value: string): void {
    accountPersistStorage.setItem(key, value)
  }

  public static removeItem(key: string): void {
    accountPersistStorage.removeItem(key)
  }

  /** Merge a new account selection into the per-user old-selection cache (pure). */
  public static setOldSelectedAccount(
    userKey: string,
    accountId: number,
    cache: Record<string, number>,
  ): Record<string, number> {
    return {
      ...cache,
      [userKey]: accountId,
    }
  }
}
